<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="full-width">
    <!--Card for list view-->
    <template v-if="isListView">
      <div
        class="card column full-height"
        @click="handleClick"
      >
        <div
          class="mention-content list-content row no-wrap full-height relative items-center"
          :class="!hideBorderColor && `border-color-${medium}`"
        >
          <QCheckbox
            v-show="showCheckbox"
            :aria-label="checkboxLabel"
            class="q-pb-md col-auto"
            data-testid="select-mention"
            :model-value="selected"
            size="27px"
            @update:model-value="$emit('mention-selected', mention)"
          />
          <div class="column col q-ml-md">
            <slot name="list-title">
              <div class="row q-mt-sm full-height flex">
                <span class="flex-1">
                  <BaseLineClamp
                    class="bold"
                    tag="span"
                  >
                    {{ listViewTitle }}
                    <template #after="{ clamped }">
                      <BaseStreemTooltip v-if="clamped">
                        {{ listViewTitle }}
                      </BaseStreemTooltip>
                    </template>
                  </BaseLineClamp>
                  <QIcon
                    v-if="isSocial && mention.author_verified"
                    class="q-ma-xs q-mb-sm"
                    color="black"
                    name="ion-checkmark-circle"
                    size="13px"
                    style="height: 0 !important"
                  />
                </span>
                <span class="row items-center">
                  <div @click.stop>
                    <slot name="list-actions" />
                  </div>

                  <Component
                    :is="actionSheetComponent"
                    class="mention-dropdown"
                    :mention="mention"
                    :mentions="mentions"
                    :origin="origin"
                    :product="product"
                    :stream="stream"
                    @mention-removed="removeMention"
                    @tags-changed-in-action-sheet="updateTags"
                    @translate="handleTranslation"
                  />
                </span>
              </div>
              <p class="q-pl-xs q-pt-xs softer">
                {{
                  formatIntlDate(mention.timestamp * 1000, {
                    day: "numeric",
                    month: "short",
                    hour: "numeric",
                    minute: "numeric",
                  })
                }}
              </p>
              <slot name="list-title-after" />
            </slot>

            <slot name="list-body">
              <TranslationLoader v-if="translatedMention.loading" />

              <template v-else>
                <MentionExcerpts
                  :always-show-keywords="alwaysShowKeywords"
                  list-view
                  :mention="mentionFormatted"
                  :no-highlighting="noHighlighting"
                />
                <slot name="list-body-after" />

                <div
                  v-if="translatedMention.error || translatedMention.success"
                  class="translation-footer"
                  @click.stop
                >
                  <div class="translation-footer-content">
                    <TranslationDescription
                      v-if="
                        translatedMention.success &&
                        translatedMention.source_language
                      "
                      :from-language="translatedMention.source_language"
                    />
                    <TranslationError
                      v-else
                      @retry="handleTranslation"
                    />
                  </div>
                </div>
              </template>
            </slot>

            <BookmarkLabels
              :bookmarks="bookmarkTags"
              :loading="mention.loadingTag"
              :mention="mention"
              @tag-deleted="deleteTag"
            />
            <slot name="list-body-append" />

            <MentionRestriction
              v-if="isMentionRestricted"
              class="tw:mt-2"
            />
          </div>
        </div>
      </div>
    </template>

    <!--Card for streams-->
    <template v-else>
      <div
        class="card mention-stream-view-card column"
        :class="[
          hideBorderTop && 'hide-radius-top',
          hideBorderBottom && 'hide-radius-bottom',
        ]"
        @click="handleClick"
      >
        <slot
          :hide-border-top="hideBorderTop"
          name="before"
        />
        <div
          class="mention-content stream-content column no-wrap relative"
          :class="[
            !hideBorderColor && `border-color-${medium}`,
            hideBorderTop && 'hide-radius-top',
            hideBorderBottom && 'hide-radius-bottom',
            (translatedMention.error || translatedMention.success) &&
              'hide-padding-bottom',
          ]"
        >
          <div class="row no-wrap">
            <QCheckbox
              v-show="showCheckbox"
              v-model="mention.selected"
              :aria-label="checkboxLabel"
              class="q-pr-xs items-baseline"
              data-testid="select-mention"
              size="xs"
              @update:model-value="$emit('mention-selected', mention)"
            />
            <div class="full-width">
              <div class="row no-wrap justify-between">
                <div class="column">
                  <template v-if="!translatedMention.loading">
                    <div
                      v-if="
                        canShowReactScore &&
                        averageScore > HIGH_REACT_SCORE_THRESHOLD
                      "
                      class="high-risk-label"
                    >
                      {{ $t("mention_card.high_risk") }}
                    </div>
                    <span
                      v-if="cardTitle"
                      class="e2e-mention-title tw:mb-2 tw:block tw:text-base tw:font-bold tw:leading-5 tw:text-denim-950"
                      :class="cardTitleClass"
                    >
                      <slot
                        name="innerTitle"
                        :title="cardTitle"
                      >
                        <BaseLineClamp
                          :lines="3"
                          tag="span"
                        >
                          {{ cardTitle }}
                          <template #after="{ clamped }">
                            <BaseStreemTooltip v-if="clamped">{{
                              cardTitle
                            }}</BaseStreemTooltip>
                          </template>
                        </BaseLineClamp>
                      </slot>
                    </span>

                    <div
                      class="tw:mb-4 tw:flex tw:flex-nowrap tw:items-start tw:justify-start"
                    >
                      <!--On mobile, we display logo with the content to save space-->
                      <div class="tw:flex tw:h-full tw:items-center">
                        <img
                          v-if="logo"
                          alt=""
                          class="logo"
                          :src="logo"
                        />
                        <QIcon
                          v-else-if="icon"
                          class="logo"
                          :name="icon"
                          size="40px"
                        />
                        <BaseIcon
                          v-else-if="baseIcon"
                          class="text-ds-denim-9 tw:mr-3"
                          :icon="baseIcon"
                          style="font-size: 36px"
                        />
                      </div>

                      <div
                        class="column col e2e-mention-details flex-basis-100"
                      >
                        <slot name="header" />
                      </div>
                    </div>
                  </template>
                </div>

                <div class="actions column items-center">
                  <Component
                    :is="actionSheetComponent"
                    v-show="!translatedMention.loading"
                    class="mention-dropdown tw:rounded-sm tw:hover:bg-denim-100"
                    :is-mention-translated="translatedMention.success"
                    :mention="mention"
                    :mentions="mentions"
                    :origin="origin"
                    :product="product"
                    :stream="stream"
                    @mention-removed="removeMention"
                    @tags-changed-in-action-sheet="updateTags"
                    @translate="handleTranslation"
                  />
                </div>
              </div>

              <TranslationLoader v-if="translatedMention.loading" />

              <template v-else>
                <slot
                  :mention-formatted="mentionFormatted"
                  name="beforeBody"
                />

                <slot
                  :mention-formatted="mentionFormatted"
                  name="body"
                >
                  <MentionExcerpts
                    v-if="displayMentionExcerpts"
                    :always-show-keywords="alwaysShowKeywords"
                    class="text-ds-denim-9"
                    expandable
                    :mention="mentionFormatted"
                    :no-highlighting="noHighlighting"
                  />
                </slot>

                <slot
                  v-if="!options.hideAfterBodySlot"
                  name="afterBody"
                />

                <slot />
                <BookmarkLabels
                  v-if="bookmarkTags.length"
                  :bookmarks="bookmarkTags"
                  :mention="mention"
                  @tag-deleted="deleteTag"
                />
                <div
                  v-if="translatedMention.error || translatedMention.success"
                  class="translation-footer"
                  @click.stop
                >
                  <div class="translation-footer-content">
                    <TranslationDescription
                      v-if="
                        translatedMention.success &&
                        translatedMention.source_language
                      "
                      :from-language="translatedMention.source_language"
                    />
                    <TranslationError
                      v-else
                      @retry="handleTranslation"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
          <slot name="afterRow" />

          <MentionRestriction
            v-if="isMentionRestricted"
            class="tw:mt-2"
          />
        </div>
      </div>

      <slot
        v-if="!options.hideAfterSlot"
        name="after"
      />
    </template>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { Notify } from "quasar";

import {
  BaseIcon,
  BaseLineClamp,
  BaseStreemTooltip,
} from "shared/components/base";
import BookmarkLabels from "shared/components/BookmarkLabels.vue";
import MentionExcerpts from "shared/components/MentionExcerpts.vue";
import MentionRestriction from "shared/components/MentionRestriction.vue";
import { UniversalPlayerControlWidget } from "shared/components/players/UniversalPlayerControlWidget";
import TranslationDescription from "shared/components/translation/TranslationDescription.vue";
import TranslationError from "shared/components/translation/TranslationError.vue";
import TranslationLoader from "shared/components/translation/TranslationLoader.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import { useTranslation } from "shared/composables/useTranslation";
import { HIGH_REACT_SCORE_THRESHOLD } from "shared/constants";
import { formatIntlDate } from "shared/helpers/date";
import {
  mentionKeywords,
  mentionSelectionLabel,
} from "shared/helpers/mentions";
import { useMentionCountsStore } from "shared/stores/mentionCounts";
import { useUniversalSearchStore } from "shared/stores/universalSearch";
import { isSocialMention } from "shared/types/mentions/guards";

import { props } from "./MentionCardProps";

export default {
  name: "MentionCard",
  components: {
    BaseIcon,
    BaseLineClamp,
    BaseStreemTooltip,
    MentionRestriction,
    MentionExcerpts,
    BookmarkLabels,
    UniversalPlayerControlWidget,
    TranslationLoader,
    TranslationError,
    TranslationDescription,
  },
  props,
  emits: ["mention-selected", "click", "mention-removed"],
  setup(context) {
    const mentionCountsStore = useMentionCountsStore();
    const { mentionCount } = storeToRefs(mentionCountsStore);

    const universalSearchStore = useUniversalSearchStore();
    const { searchOpen } = storeToRefs(universalSearchStore);

    const { translateMention, translatedMention } = useTranslation(
      context.mention
    );

    const { selectedTeamPreference } = useTeamPicker();

    return {
      mentionCount,
      searchOpen,

      translateMention,
      translatedMention,
      selectedTeamPreference,
    };
  },
  data() {
    return {
      HIGH_REACT_SCORE_THRESHOLD,
      bookmarkTags: [],
    };
  },
  computed: {
    isMentionRestricted() {
      return this.mention.outside_source_retention_period;
    },
    canShowReactScore() {
      return this.$features.has("has_react_score");
    },
    reactScores() {
      return this.mention.factmata_enrichment || {};
    },
    averageScore() {
      return Math.round(this.reactScores.risk_score * 100);
    },
    isSocial() {
      return isSocialMention(this.mention);
    },
    mentionType() {
      if (this.mention.type === "transcript_request") {
        return this.mention.source.type;
      }

      return this.mention.type;
    },
    medium() {
      return this.mention.medium?.toLowerCase();
    },
    hideBorderTop() {
      return (
        !this.options.noSquareBorders &&
        !this.options.noSquareTopBorder &&
        this.mentions[0] === this.mention &&
        Boolean(this.mentionCount(this.stream.id))
      );
    },
    hideBorderBottom() {
      return (
        !this.options.noSquareBorders &&
        (this.canShowRelatedArticle ||
          (this.mention.similarMentions && this.mention.similarMentions.length))
      );
    },
    canShowRelatedArticle() {
      if (this.mention.related_article) {
        if (this.mention.type === "paper_article") {
          return this.$features.has("read_online_article");
        }

        return this.$features.has("view_paper_articles");
      }

      return false;
    },
    isListView() {
      return this.options && this.options.list;
    },
    displayMentionExcerpts() {
      return (
        ![
          "journalist",
          "tv_media_request",
          "radio_media_request",
          "transcript_request",
          "TvChannel",
          "RadioStation",
        ].includes(this.mentionType) && !this.options.hideExcerpts
      );
    },
    origin() {
      const originKeyword = mentionKeywords(this.mention)[0] || "";

      if (this.searchOpen) {
        return {
          origin_type: "Search",
          origin_keyword: originKeyword,
        };
      }

      if (this.stream.id) {
        return {
          origin_id: this.stream.id,
          origin_type: "Stream",
          origin_keyword: originKeyword,
        };
      }

      if (this.stream.folderId) {
        return {
          origin_id: this.stream.folderId,
          origin_type: "Folder",
          origin_keyword: originKeyword,
        };
      }

      return {};
    },
    // Name the checkbox after whichever title the current view renders, so it
    // matches what is on screen. TV and radio mentions carry no headline, so
    // the card title is empty for them; the list title names the programme and
    // location ("National Nine News Darwin") instead.
    checkboxLabel() {
      const title = this.isListView
        ? this.listViewTitle
        : this.cardTitle || this.listViewTitle;

      return mentionSelectionLabel(title);
    },
    cardTitle() {
      if (this.translatedMention.success) {
        return this.translatedMention.title;
      }

      return this.title;
    },
    listViewTitle() {
      if (this.translatedMention.success) {
        return this.translatedMention.title;
      }

      return this.listTitle || this.mention.title;
    },
    mentionFormatted() {
      if (this.translatedMention.success) {
        const { title, excerpts } = this.translatedMention;

        return {
          ...this.mention,
          title,
          excerpts,
        };
      }

      return this.mention;
    },
  },
  watch: {
    "mention.bookmarks": {
      handler: "resetBookmarks",
      deep: true,
    },
  },
  mounted() {
    this.resetBookmarks();
  },
  methods: {
    formatIntlDate,
    resetBookmarks() {
      if (this.$features.has("has_team_workspaces")) {
        this.bookmarkTags = (this.mention.bookmarks || []).filter(
          (bookmark) =>
            bookmark.organisation_team_id === this.selectedTeamPreference ||
            !bookmark.organisation_team_id
        );
      } else {
        this.bookmarkTags = this.mention.bookmarks || [];
      }
    },
    removeMention(mention) {
      this.$emit("mention-removed", mention);
    },
    updateTags(bookmarks) {
      this.bookmarkTags = bookmarks;
    },
    deleteTag(tagId) {
      this.bookmarkTags = this.bookmarkTags.filter((tag) => tag.id !== tagId);
    },
    handleClick() {
      this.$emit("click", this.mention);
    },
    async handleTranslation() {
      if (this.translatedMention.loading || this.translatedMention.success) {
        return;
      }

      await this.translateMention(this.mention);

      if (!this.translatedMention.success) {
        return;
      }

      Notify.create({
        message: this.$t("mention_card.mention_translated"),
        actions: [
          {
            icon: "close",
            color: "white",
          },
        ],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.high-risk-label {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: $react-score-warning;
  color: white;
  width: 70px;
  height: 20px;
  border-radius: 19px;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.actions {
  margin-top: -8px;

  :deep(.q-btn__wrapper) {
    min-width: 0 !important;
    min-height: 0 !important;
  }

  :deep(.play-button) {
    height: auto;
  }
}

.q-checkbox {
  :deep(.q-checkbox__bg) {
    border: 1px solid #3e475e;
  }
}

.e2e-mention-title {
  word-break: break-word;
  letter-spacing: 0.02em;
}

:deep(.muted) {
  font-size: 80%;
  line-height: 100%;
  opacity: 0.5;
  color: #444;
  font-weight: normal;
}

:deep(.pull-right) {
  float: right;
}

:deep(.publishers-list) {
  background-color: #eee;
  padding: 5px;
  margin-top: 5px;
  margin-bottom: 5px;

  > a,
  > span {
    width: 100%;
    display: inline-block;
  }
}

:deep(.mention-content) {
  border-left: 5px solid transparent;
  width: 100%;

  &.stream-content {
    border-radius: 4px;
    padding: 16px;
  }

  &.list-content {
    padding: 0 10px 10px;
  }
}

:deep(.syndication-time) {
  margin-top: 5px;
  margin-left: auto;
  margin-right: 5px;
  white-space: nowrap;
  padding-left: 5px;
}

:deep(.syndication-matches-stream) {
  display: inline-block;
  width: 0.8em;
}

:deep(.card) {
  &.syndication-card {
    margin-top: 2px;
  }

  p {
    margin-bottom: 5px;
    line-height: 20px;
  }

  p em {
    background-color: rgb(252 220 89 / 30%);
    padding: 1px 2px;
    font-style: normal;
  }

  .logo {
    width: 40px;
    margin-right: 10px;
    color: #767676;
  }

  .thumbnail {
    width: 50%;
    object-fit: cover;
    object-position: 50% 30%;
    height: 80px;
    border-bottom: 1px solid $ds-denim-3;

    &:nth-child(1) {
      border-top-left-radius: 4px;
    }

    &:nth-child(2) {
      border-top-right-radius: 4px;
    }
  }

  &.warning {
    h3 {
      margin: 12px 0;
    }

    .q-icon {
      vertical-align: -3px;
    }
  }

  .ion-checkmark-circle {
    color: rgb(73 208 103 / 50%);
  }

  .ion-close-circle {
    color: rgb(249 88 88 / 50%);
  }

  .ion-remove-circle {
    color: rgb(50 159 236 / 50%);
  }
}

:deep(.q-item-label) {
  color: yellow;
}

.youtube {
  :deep(.logo.q-icon) {
    height: auto;
  }
}

.translation-footer {
  margin-top: 16px;
  margin-left: -15px;
  margin-right: -15px;
  border-top: 1px solid;
  border-color: var(--s-color-denim-3);
  cursor: text;

  &-content {
    padding: 16px;
  }
}

.hide-padding-bottom {
  padding-bottom: 0 !important;
}
</style>
